/**
 * AttendanceSummaryReport — Raport i konsoliduar i attendance
 * Vonesa, early checkout, overtime, orë totale, pauzat
 * Bazuar te attendance_sessions + work_schedules (JO time_entries / projekte)
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Avatar, Chip, Button, TextField, MenuItem, Stack, CircularProgress, Tabs, Tab } from '@mui/material';
import SearchIcon         from '@mui/icons-material/Search';
import LoginIcon          from '@mui/icons-material/Login';
import LogoutIcon         from '@mui/icons-material/Logout';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import MoreTimeIcon       from '@mui/icons-material/MoreTime';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import CoffeeIcon         from '@mui/icons-material/Coffee';
import * as XLSX          from 'xlsx';
import { useTranslation } from 'react-i18next';
import jsPDF              from 'jspdf';
import autoTable          from 'jspdf-autotable';
import apiService         from '../../services/apiServices';
import { computeAttendanceSummary } from '../../services/attendanceDemoService';
import EmployeeDailyDetail from './EmployeeDailyDetail';

import { getAuthHeaders } from '../../services/authSession';
import { API_URL, isDemoMode } from '../../services/backendMode';
import { ReportPage, ExportMenu } from '../report/ReportKit';
import { activeWorkers } from '../../utils/workerStatus';

async function fetchSummary(params) {
  if (isDemoMode()) {
    const data = computeAttendanceSummary(params);
    return { success: true, data };
  }
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v))
  ).toString();
  const res = await fetch(`${API_URL}/api/reports/attendance-summary?${qs}`, {
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  });
  return res.json().catch(() => ({ success: false, data: [] }));
}

// Dita LOKALE: `toISOString()` është UTC dhe para orës 02:00 jepte ditën e djeshme.
const localYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayStr   = () => localYMD(new Date());
const monthStart = () => { const d = new Date(); d.setDate(1); return localYMD(d); };

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' });
}

function fmtMin(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (m === 0) return '—';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
}

function initials(name) {
  return (name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

// Chip per compliance status
function CompChip({ status, lateMin, earlyMin }) {
  const { t } = useTranslation();
  const configs = {
    absent:         { label: t('report.absentLabel'),    color: 'error' },
    in_progress:    { label: t('report.inProgress'),     color: 'primary' },
    late:           { label: `${t('report.lateLabel')}${lateMin > 0 ? ` +${fmtMin(lateMin)}` : ''}`, color: 'warning' },
    early_checkout: { label: `${t('report.earlyCheckout')} -${fmtMin(earlyMin)}`, color: 'warning' },
    on_time:        { label: t('report.onTime'),         color: 'success' },
  };
  const cfg = configs[status] || { label: status || '—', color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

// Aggregate per worker
function aggregateByWorker(rows) {
  const map = {};
  rows.forEach(r => {
    const k = String(r.worker_id);
    if (!map[k]) map[k] = {
      worker_id:      r.worker_id,
      worker_name:    r.worker_name,
      department_name:r.department_name,
      days:           0,
      absent:         0,
      late:           0,
      early_checkout: 0,
      on_time:        0,
      overtime_days:  0,
      total_late_min: 0,
      total_early_min:0,
      total_overtime_min: 0,
      total_worked_min:   0,
      total_break_min:    0,
    };
    const w = map[k];
    w.days++;
    if (r.compliance_status === 'absent')         w.absent++;
    else if (r.compliance_status === 'late')      { w.late++;          w.total_late_min    += Number(r.late_minutes    || 0); }
    else if (r.compliance_status === 'early_checkout') { w.early_checkout++; w.total_early_min += Number(r.early_checkout_minutes || 0); }
    else if (r.compliance_status === 'on_time')   w.on_time++;
    if (Number(r.overtime_minutes) > 0)           { w.overtime_days++; w.total_overtime_min += Number(r.overtime_minutes || 0); }
    w.total_worked_min += Number(r.worked_minutes || 0);
    w.total_break_min  += Number(r.break_minutes  || 0);
  });
  return Object.values(map).sort((a, b) => a.worker_name?.localeCompare(b.worker_name));
}

// ── Summary card ─────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color }) {
  return (
    <Paper elevation={2} sx={{ p: 2, borderRadius: 3, textAlign: 'center' }}>
      <Box sx={{ color: `${color}.main`, mb: 0.5 }}>{icon}</Box>
      <Typography variant="h5" fontWeight={700} color={`${color}.main`}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

export default function AttendanceSummaryReport() {
  const { t } = useTranslation();
  const [tab,         setTab]         = useState(0); // 0=ditore, 1=përmbledhje
  const [dateFrom,    setDateFrom]    = useState(monthStart());
  const [dateTo,      setDateTo]      = useState(todayStr());
  const [fWorker,     setFWorker]     = useState('');
  const [fDept,       setFDept]       = useState('');
  const [workers,     setWorkers]     = useState([]);
  const [departments, setDepts]       = useState([]);
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(false);

  // Detail dialog state
  const [detailOpen,  setDetailOpen]  = useState(false);
  const [detailWorker, setDetailWorker] = useState({ id: null, name: '' });
  const [detailYear,  setDetailYear]  = useState(null);
  const [detailMonth, setDetailMonth] = useState(null);

  function openDetail(ws) {
    // ws = worker summary row
    setDetailWorker({ id: ws.worker_id, name: ws.worker_name });
    // derive year/month from current dateFrom filter
    const d = new Date(dateFrom || new Date());
    setDetailYear(d.getFullYear());
    setDetailMonth(d.getMonth() + 1);
    setDetailOpen(true);
  }

  // Load reference data once
  useEffect(() => {
    Promise.all([
      apiService().get('workers'),
      apiService().get('departments'),
    ]).then(([wRes, dRes]) => {
      if (wRes?.data) setWorkers(wRes.data);
      if (dRes?.data) setDepts(dRes.data);
    }).catch(console.error);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchSummary({
        from:          dateFrom,
        to:            dateTo,
        worker_id:     fWorker     || undefined,
        department_id: fDept       || undefined,
      });
      setRows(res?.data || []);
    } catch (e) {
      console.error('[AttendanceSummaryReport]', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, fWorker, fDept]);

  // Summary stats
  const stats = useMemo(() => {
    const total   = rows.length;
    const late    = rows.filter(r => Number(r.late_minutes) > 0).length;
    const early   = rows.filter(r => Number(r.early_checkout_minutes) > 0).length;
    const ot      = rows.filter(r => Number(r.overtime_minutes) > 0).length;
    const absent  = rows.filter(r => r.compliance_status === 'absent').length;
    return { total, late, early, ot, absent };
  }, [rows]);

  // Worker summary for tab 1
  const workerSummary = useMemo(() => aggregateByWorker(rows), [rows]);

  // Auto-load on mount
  useEffect(() => { load(); }, [load]);

  // Export Excel
  function exportXlsx() {
    const data = rows.map(r => ({
      [t('report.date')]:                r.date,
      [t('report.worker')]:              r.worker_name,
      [t('report.department')]:          r.department_name,
      [t('report.scheduleCol')]:         r.schedule_name,
      [t('report.checkIn')]:             fmtTime(r.check_in),
      [t('report.checkOut')]:            fmtTime(r.check_out),
      [`${t('report.breakCol')} (min)`]: r.break_minutes,
      [t('report.netHoursCol')]:         fmtMin(r.net_worked_minutes),
      [`${t('report.latenessCol')} (min)`]:    r.late_minutes,
      [`${t('report.earlyCheckout')} (min)`]:  r.early_checkout_minutes,
      [`${t('report.overtime')} (min)`]:       r.overtime_minutes,
      [t('report.status')]:              r.compliance_status,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    XLSX.writeFile(wb, `attendance-${dateFrom}-${dateTo}.xlsx`);
  }

  // Export PDF
  function exportPdf() {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text(`${t('report.attendanceReport')} — ${dateFrom} ${t('common.to')} ${dateTo}`, 14, 15);
    autoTable(doc, {
      startY: 22,
      head: [[t('report.date'), t('report.worker'), t('report.department'), t('report.checkIn'), t('report.checkOut'), t('report.breakCol'), t('report.netHoursCol'), t('report.latenessCol'), t('report.earlyCheckout'), t('report.overtime'), t('report.status')]],
      body: rows.map(r => [
        r.date, r.worker_name, r.department_name || '',
        fmtTime(r.check_in), fmtTime(r.check_out),
        fmtMin(r.break_minutes), fmtMin(r.net_worked_minutes),
        fmtMin(r.late_minutes), fmtMin(r.early_checkout_minutes),
        fmtMin(r.overtime_minutes), r.compliance_status || '',
      ]),
      styles: { fontSize: 7 },
    });
    doc.save(`attendance-${dateFrom}-${dateTo}.pdf`);
  }

  return (
    <ReportPage>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h5" fontWeight={700}>
          Raporti i Attendance
        </Typography>
        <Stack direction="row" spacing={1}>
          <ExportMenu onExcel={exportXlsx} onPdf={exportPdf} />
        </Stack>
      </Stack>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" gap={1} alignItems="flex-end">
          <TextField type="date" size="small" label={t('report.from')} value={dateFrom}
            onChange={e => setDateFrom(e.target.value)} sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
          <TextField type="date" size="small" label={t('report.to')} value={dateTo}
            onChange={e => setDateTo(e.target.value)} sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
          <TextField select size="small" label={t('report.worker')} value={fWorker}
            onChange={e => setFWorker(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">{t('report.allWorkers')}</MenuItem>
            {activeWorkers(workers).map(w => (
              <MenuItem key={w.Id || w.id} value={String(w.Id || w.id)}>
                {w.FirstName || w.first_name || ''} {w.LastName || w.last_name || ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label={t('report.department')} value={fDept}
            onChange={e => setFDept(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">{t('report.all')}</MenuItem>
            {departments.map(d => (
              <MenuItem key={d.Id || d.id} value={String(d.Id || d.id)}>
                {d.Name || d.name}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<SearchIcon />} onClick={load} disabled={loading}
            sx={{ borderRadius: 2 }}>
            {loading ? <CircularProgress size={18} color="inherit" /> : t('report.search')}
          </Button>
        </Stack>
      </Paper>

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: t('report.registrations'), value: stats.total,  icon: <CheckCircleIcon />, color: 'success' },
          { label: t('report.latenessCard'),   value: stats.late,   icon: <WarningAmberIcon />, color: 'warning' },
          { label: t('report.earlyCheckout'),  value: stats.early,  icon: <LogoutIcon />,       color: 'warning' },
          { label: t('report.overtime'),       value: stats.ot,     icon: <MoreTimeIcon />,     color: 'info' },
          { label: t('report.absentLabel'),    value: stats.absent, icon: <LoginIcon />,        color: 'error' },
        ].map((s) => (
          <Grid item xs={6} sm={4} md={2.4} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={t('report.dailyRecords')} />
          <Tab label={t('report.workerSummary')} />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : tab === 0 ? (
          /* ── Daily records ── */
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>{t('report.date')}</TableCell>
                  <TableCell>{t('report.worker')}</TableCell>
                  <TableCell>{t('report.department')}</TableCell>
                  <TableCell>{t('report.scheduleCol')}</TableCell>
                  <TableCell>{t('report.checkIn')}</TableCell>
                  <TableCell>{t('report.checkOut')}</TableCell>
                  <TableCell>{t('report.breakCol')}</TableCell>
                  <TableCell align="right">{t('report.netHoursCol')}</TableCell>
                  <TableCell align="right">{t('report.latenessCol')}</TableCell>
                  <TableCell align="right">{t('report.earlyCheckout')}</TableCell>
                  <TableCell align="right">{t('report.overtime')}</TableCell>
                  <TableCell>{t('report.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r.id || i} hover>
                    <TableCell><Typography variant="body2">{r.date}</Typography></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'primary.light' }}>
                          {initials(r.worker_name)}
                        </Avatar>
                        <Typography variant="body2">{r.worker_name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{r.department_name || '—'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {r.schedule_name
                          ? `${r.schedule_name} (${r.scheduled_start?.slice(0,5) || ''}–${r.scheduled_end?.slice(0,5) || ''})`
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell><Stack direction="row" spacing={0.5} alignItems="center"><LoginIcon sx={{ fontSize: 13, color: 'success.main' }} /><Typography variant="body2">{fmtTime(r.check_in)}</Typography></Stack></TableCell>
                    <TableCell><Stack direction="row" spacing={0.5} alignItems="center"><LogoutIcon sx={{ fontSize: 13, color: 'error.main' }} /><Typography variant="body2">{fmtTime(r.check_out)}</Typography></Stack></TableCell>
                    <TableCell><Stack direction="row" spacing={0.5} alignItems="center"><CoffeeIcon sx={{ fontSize: 13, color: 'warning.main' }} /><Typography variant="body2">{fmtMin(r.break_minutes)}</Typography></Stack></TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight={500}>{fmtMin(r.net_worked_minutes)}</Typography></TableCell>
                    <TableCell align="right">
                      {Number(r.late_minutes) > 0
                        ? <Typography variant="body2" color="warning.main" fontWeight={500}>+{fmtMin(r.late_minutes)}</Typography>
                        : <Typography variant="body2" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      {Number(r.early_checkout_minutes) > 0
                        ? <Typography variant="body2" color="warning.main" fontWeight={500}>-{fmtMin(r.early_checkout_minutes)}</Typography>
                        : <Typography variant="body2" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      {Number(r.overtime_minutes) > 0
                        ? <Typography variant="body2" color="info.main" fontWeight={500}>+{fmtMin(r.overtime_minutes)}</Typography>
                        : <Typography variant="body2" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell>
                      <CompChip status={r.compliance_status} lateMin={r.late_minutes} earlyMin={r.early_checkout_minutes} />
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">
                        {t('report.noDataClickSearch')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          /* ── Worker summary ── */
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>{t('report.worker')}</TableCell>
                  <TableCell>{t('report.department')}</TableCell>
                  <TableCell align="right">{t('report.days')}</TableCell>
                  <TableCell align="right">{t('report.absentLabel')}</TableCell>
                  <TableCell align="right">{t('report.latenessCard')}</TableCell>
                  <TableCell align="right">{t('report.earlyCheckout')}</TableCell>
                  <TableCell align="right">{t('report.overtime')}</TableCell>
                  <TableCell align="right">{t('report.lateMinutes')}</TableCell>
                  <TableCell align="right">{t('report.earlyMinutes')}</TableCell>
                  <TableCell align="right">{t('report.extraMinutes')}</TableCell>
                  <TableCell align="right">{t('report.netHoursCol')}</TableCell>
                  <TableCell align="right">{t('report.totalBreak')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workerSummary.map((ws, i) => (
                  <TableRow key={ws.worker_id || i} hover
                    onClick={() => openDetail(ws)}
                    sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'secondary.light' }}>
                          {initials(ws.worker_name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{ws.worker_name}</Typography>
                          <Typography variant="caption" color="primary.main">{t('report.clickForDetails')}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{ws.department_name || '—'}</Typography></TableCell>
                    <TableCell align="right">{ws.days}</TableCell>
                    <TableCell align="right">
                      {ws.absent > 0
                        ? <Typography color="error.main" fontWeight={600}>{ws.absent}</Typography>
                        : <Typography color="text.disabled">0</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      {ws.late > 0
                        ? <Typography color="warning.main" fontWeight={600}>{ws.late}</Typography>
                        : <Typography color="text.disabled">0</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      {ws.early_checkout > 0
                        ? <Typography color="warning.main" fontWeight={600}>{ws.early_checkout}</Typography>
                        : <Typography color="text.disabled">0</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      {ws.overtime_days > 0
                        ? <Typography color="info.main" fontWeight={600}>{ws.overtime_days}</Typography>
                        : <Typography color="text.disabled">0</Typography>}
                    </TableCell>
                    <TableCell align="right"><Typography variant="body2" color={ws.total_late_min > 0 ? 'warning.main' : 'text.disabled'}>{fmtMin(ws.total_late_min)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" color={ws.total_early_min > 0 ? 'warning.main' : 'text.disabled'}>{fmtMin(ws.total_early_min)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" color={ws.total_overtime_min > 0 ? 'info.main' : 'text.disabled'}>{fmtMin(ws.total_overtime_min)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight={500}>{fmtMin(ws.total_worked_min)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" color="text.secondary">{fmtMin(ws.total_break_min)}</Typography></TableCell>
                  </TableRow>
                ))}
                {workerSummary.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">
                        {t('report.noDataClickSearch')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Employee daily detail dialog */}
      <EmployeeDailyDetail
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        workerId={detailWorker.id}
        workerName={detailWorker.name}
        initialYear={detailYear}
        initialMonth={detailMonth}
      />
    </ReportPage>
  );
}
